// Tracks anything in the page that has unsaved-but-pending changes — most
// importantly note autosaves. Used by the page-level lifecycle handlers
// (turbo:before-visit, beforeunload, pagehide) to flush before the DOM
// or the page goes away. Pure module: no DOM imports, easy to unit-test.
//
// An entry is anything implementing:
//   hasPending()      : boolean              // active work (queued / in-flight / retry timer)
//   hasUnresolved()   : boolean              // hasPending OR last save failed without recovery
//   flushPending()    : Promise<boolean>     // true iff converged with server after flush
//   flushBeacon()     : void   (optional)    // unload — synchronous, fire-and-forget

const entries = new Set()

export function register(entry) {
    entries.add(entry)
    return () => entries.delete(entry)
}

export function hasAnyPending() {
    for (const e of entries) {
        if (e.hasPending()) return true
    }
    return false
}

// Broader than hasAnyPending — true if any entry has local content the
// server hasn't accepted, including a save that failed non-retryably and
// hasn't been recovered. Navigation guards should use this so a stale 412
// or dead 403 isn't silently lost when Turbo replaces the DOM.
export function hasAnyUnresolved() {
    for (const e of entries) {
        if (e.hasUnresolved()) return true
    }
    return false
}

// Drives every entry with unresolved local content through its flush, then
// reports whether all of them landed. Resolves rather than rejects on
// per-entry errors — the caller (turbo:before-visit) cares about the
// converged/not-converged answer, not which entry threw.
export async function flushAll() {
    const flushes = []
    for (const e of entries) {
        if (e.hasUnresolved()) flushes.push(Promise.resolve(e.flushPending()))
    }
    const results = await Promise.allSettled(flushes)
    return results.every((r) => r.status === "fulfilled" && r.value === true)
}

// Best-effort synchronous flush — appropriate for pagehide and
// visibilitychange where the browser will not wait for promises.
// Each entry is responsible for using keepalive / sendBeacon under the
// hood. Errors are swallowed since there is nothing to surface to.
// Uses the narrower hasPending check: re-beaconing a save the server
// already rejected (412 stale) would just produce another rejection.
export function beaconAll() {
    for (const e of entries) {
        if (!e.hasPending() || typeof e.flushBeacon !== "function") continue
        try { e.flushBeacon() } catch (_) { /* nothing to do */ }
    }
}

export function _entriesForTest() {
    return entries
}
